import { useState, useEffect, useMemo } from "react";
import { Download, ChevronDown, Save, Plus, Trash2, Check, Ban, CheckCircle2, FileText } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import AstrafinLogo from "../../assets/logo.svg";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { cn } from "../../lib/utils";
import type { TermsheetData } from "../../data/termsheetData";
import { generateTermsheetPDF } from "../../utils/generateTermSheetPDF";
import { generateTermsheetDocx } from "../../utils/generateTermSheetDocx";
import apiService from "../../services/apiService";
import { toast } from 'react-toastify';
import { Drawer, DrawerContent, DrawerTitle } from "../ui/drawer";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useCustomerWorkflow } from "../../context/CustomerWorkflowContext";

/** Load Astrafin logo (SVG) and return as high-resolution PNG data URL for use in jsPDF. */
function getLogoDataUrl(): Promise<string | undefined> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const scale = 4; // 4x resolution so logo stays sharp when zooming in PDF
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth * scale;
        canvas.height = img.naturalHeight * scale;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve(undefined);
          return;
        }
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/png"));
      } catch {
        resolve(undefined);
      }
    };
    img.onerror = () => resolve(undefined);
    img.src = AstrafinLogo;
  });
}


interface ApiResponse<T> {
  data?: T;
  message?: string;
  status?: number;
}

export interface TermsheetSavedValues {
  programLimit?: string;
  customRows?: CustomRow[];
  proRatedDay?: number;
  additionalSecurity?: string;
  status?: Status;
  termSheetId?: string;
  downloadedAt?: string;
}

interface Props {
  drawerOpen: boolean;
  setDrawerOpen: (open: boolean) => void;
  onClose: () => void;
  selectedCalculation: any;
  companyName: string;
  companyCity: string;
  customerId?: string;
  onSaveSuccess?: (updated: TermsheetSavedValues) => void;
  termSheetId?: string;
  /** Optional header (e.g. title + status) rendered inside the drawer above main content */
  optionalHeaderSlot?: React.ReactNode;
}

type Status = "draft" | "approved" | "rejected" | "shared" | "review";

interface CustomRow {
  label: string;
  value: string;
}

const statusConfig: Record<Status, { label: string; className: string }> = {
  draft: { label: "Draft", className: "bg-muted text-muted-foreground border-border" },
  approved: { label: "Approved", className: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30" },
  rejected: { label: "Rejected", className: "bg-destructive/15 text-destructive border-destructive/30" },
  shared: { label: "Shared", className: "bg-[hsl(var(--info))]/15 text-[hsl(var(--info))] border-[hsl(var(--info))]/30" },
  review : { label: "Draft", className: "bg-muted text-muted-foreground border-border" },
};

/** Format a numeric string or number into Indian lakhs/crores notation */
const formatIndianCurrency = (value: string | number | null | undefined): string => {
  const str = value != null ? String(value) : "";
  const cleaned = str.replace(/[^0-9.]/g, "");
  const num = parseFloat(cleaned);
  if (isNaN(num)) return str;
  if (num >= 1_00_00_000) {
    const cr = num / 1_00_00_000;
    return `₹${cr % 1 === 0 ? cr.toFixed(0) : cr.toFixed(2)} Cr`;
  }
  if (num >= 1_00_000) {
    const l = num / 1_00_000;
    return `₹${l % 1 === 0 ? l.toFixed(0) : l.toFixed(2)} L`;
  }
  return `₹${num.toLocaleString("en-IN")}`;
};

/** Parse program limit string or number to a number (digits and decimal only). */
const getProgramLimitNumeric = (value: string | number | null | undefined): number => {
  const str = value != null ? String(value) : "";
  const cleaned = str.replace(/[^0-9.]/g, "");
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
};

/** Parse security deposit percentage from string e.g. "10%" -> 10. */
const getSecurityDepositPct = (value: string | number | null | undefined): number => {
  const str = value != null ? String(value) : "";
  const cleaned = str.replace(/[^0-9.]/g, "");
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
};

/** Format date string as DD/MM/YYYY. */
const formatDateDDMMYYYY = (dateStr: string | null | undefined): string => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return String(dateStr);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

/** Ordinal for day: 1 -> "1st", 2 -> "2nd", 3 -> "3rd", 4 -> "4th", ... */
const dayOrdinal = (day: number): string => {
  if (day >= 11 && day <= 13) return `${day}th`;
  switch (day % 10) {
    case 1: return `${day}st`;
    case 2: return `${day}nd`;
    case 3: return `${day}rd`;
    default: return `${day}th`;
  }
};

const getRentalPaymentsText = (dueDay: number, monthQuarterText: string, monthQuarterText2: string): string =>
  `The Lessee shall provide and maintain a valid NACH mandate in favour of a designated collection account. Rentals shall be payable ${monthQuarterText2} in advance and shall fall due on the ${dayOrdinal(dueDay)} day of each calendar ${monthQuarterText}. The Lessor shall initiate debit instructions on the due date for administrative convenience.\n\nAny delay in the payment of the rentals shall attract overdue interest on the rentals due but unpaid at the overdue rate mentioned in the Master Rental Agreement.`;

const getProRatedRentalsText = (dueDay: number, monthQuarterText: string, monthQuarterText2: string, days: string): string => {
  const dueStr = dayOrdinal(dueDay);
  const untilPhrase = dueDay === 1
    ? "last day of the previous month"
    : `${dayOrdinal(dueDay - 1)} day of the relevant ${monthQuarterText}`;
  return `For administrative convenience, where the commencement date falls on a day other than the ${dueStr} day of the ${monthQuarterText}, the Lessee shall pay a pro-rated rental calculated at ${days}th of the ${monthQuarterText2} rental for each day from the commencement date until the ${untilPhrase}. Rentals for the full ${monthQuarterText2} period shall thereafter be payable in advance on the ${dueStr} day of each ${monthQuarterText}. Rentals shall commence from the invoice date.`;
};

/** Format number as "INR X.XX lakhs" or "INR X.XX crores" for security deposit display. */
const formatInrLakhsCrores = (num: number): string => {
  if (num >= 1_00_00_000) {
    const cr = num / 1_00_00_000;
    return `INR ${cr % 1 === 0 ? cr.toFixed(0) : cr.toFixed(2)} crores`;
  }
  if (num >= 1_00_000) {
    const l = num / 1_00_000;
    return `INR ${l % 1 === 0 ? l.toFixed(0) : l.toFixed(2)} lakhs`;
  }
  return `INR ${num.toLocaleString("en-IN")}`;
};

const formatAdvanceRentalDisplay = (
  advanceRentalEnabled?: boolean,
  advanceRentalType?: string,
  advanceRentalCount?: number,
  rentalFrequency?: string
): string => {
  if (!advanceRentalEnabled) return "";
  const count = Number(advanceRentalCount ?? 0);
  if (advanceRentalType === "percentage") {
    return `${count}%  of the utilised program limit`;
  }
  const isQuarterly = rentalFrequency === "quarterly";
  const singular = isQuarterly ? "Quarter" : "Month";
  const plural = isQuarterly ? "Quarters" : "Months";
  const normalizedCount = Number.isInteger(count) ? count : Number(count.toFixed(2));
  const unit = normalizedCount === 1 ? singular : plural;
  return `${normalizedCount} ${unit}`;
};

const TermsheetDrawer = ({ drawerOpen, setDrawerOpen, onClose, selectedCalculation, companyName, companyCity, customerId, onSaveSuccess, termSheetId, optionalHeaderSlot }: Props) => {

  const [term_sheet_id, setterm_sheet_id] = useState<string | null>(termSheetId ?? null);

  const initialData = useMemo((): TermsheetData => {
    const monthQuarterText = selectedCalculation?.inputs?.rentalFrequency === "monthly" ? "month" : "quarter";
    const monthQuarterText2 = selectedCalculation?.inputs?.rentalFrequency === "monthly" ? "monthly" : "quarterly";
    const days = selectedCalculation?.inputs?.rentalFrequency === "monthly" ? "1/30" : "1/90";
    const proRatedDay = selectedCalculation?.proRatedDay ?? 5;
    const rawDownloaded =
      selectedCalculation?.downloadedAt ?? selectedCalculation?.summary?.createdAt ?? "";
    const normalizedDate =
      rawDownloaded instanceof Date
        ? rawDownloaded.toISOString()
        : rawDownloaded
          ? String(rawDownloaded)
          : "";
    return {
      lesseeName: companyName,
      lesseeAddress: companyCity,
      lessor: 'Astrafin Neosolutions Private Limited ("ANPL")',
      date: normalizedDate,
      refNo:
        (term_sheet_id || selectedCalculation?.termSheetId
          ? String(term_sheet_id ?? selectedCalculation?.termSheetId ?? "")
          : selectedCalculation?.calculationId) ?? "",
      tenureOfLease: selectedCalculation?.inputs?.totalMonths,
      nonCancellableTerm: selectedCalculation?.inputs?.totalMonths,
      programLimit: selectedCalculation?.programLimit,
      securityDeposit: (selectedCalculation?.inputs?.securityDepositPercentage ?? 0) + "% of the utilised program limit",
      rentalCostPTPM: `₹${selectedCalculation?.inputs?.ptpmConsideration ?? ""}`,
      gstRebate: `₹${selectedCalculation?.inputs?.gstPassback ?? ""}`,
      finalPTPM: `₹${ selectedCalculation?.inputs?.gstPassbackEnabled ? selectedCalculation?.inputs?.rs_ptpm : selectedCalculation?.inputs?.finalPTPM ?? ""}`,
      processingFees: (selectedCalculation?.inputs?.processingFeePercentage ?? 0) + "%",
      advanceRental: formatAdvanceRentalDisplay(
        selectedCalculation?.inputs?.advanceRentalEnabled,
        selectedCalculation?.inputs?.advanceRentalType,
        selectedCalculation?.inputs?.advanceRentalCount,
        selectedCalculation?.inputs?.rentalFrequency
      ),
      paymentFrequency: selectedCalculation?.inputs?.rentalFrequency === "monthly" ? "Monthly in Advance" : "Quarterly in Advance",
      rentalPayments: getRentalPaymentsText(proRatedDay, monthQuarterText, monthQuarterText2),
      proRatedRentals: getProRatedRentalsText(proRatedDay, monthQuarterText, monthQuarterText2, days),
      proRatedRentalsDate: `${dayOrdinal(proRatedDay)} day of each ${monthQuarterText}`,
      foreclosureCharges:
        "In the event of any prepayment, foreclosure, or early termination of the Lease by the Lessee, the outstanding rentals and any applicable termination amounts shall be settled in accordance with the agreed foreclosure methodology.",
      documentationCharges:
        "All costs and expenses related to documentation, including but not limited to agreement drafting, stamping, registration, and execution, shall be borne exclusively by the Customer.",
      insurance:
        "Insurance for the Assets shall be maintained by the Lessor for the entire term of the Lease. In case the Lessor is required to procure such insurance on behalf of the Lessee, the Lessee shall bear all costs and premiums relating to the insurance of the Assets for the lease term, payable at actuals against debit notes raised by the Lessor.\n\nMarine/transit insurance, where applicable, shall be procured by the Lessee directly at its own cost, unless otherwise agreed in writing by the Parties.",
      clearTitle:
        "The Lessee represents and warrants that the Assets proposed to be leased or sold and leased back shall be free from any lien, charge, encumbrance, pledge, or third-party interest at the time of transfer to the Lessor. In the case of sale and leaseback transactions, where the Assets are subject to any existing working capital or other financing arrangements, the Lessee shall procure and provide a no-objection certificate (NOC) from the relevant lender prior to consummation of the transaction.",
      economicLife: "Economic life to be certified by the Equipment Seller / OEM",
      penalCharges:
        "Without prejudice to the Lessor's other rights and remedies under this Agreement, any delay in payment of rentals or any other amounts due and payable by the Lessee shall attract penal charges in the form of overdue interest at the rate specified in the relevant Rental Schedule or the MRA, calculated from the due date until the date of actual payment.\n\nSuch penal charges shall be payable on demand and shall not be construed as a waiver of any Event of Default or any other rights available to the Lessor under this Agreement.",
      additionalSecurity: selectedCalculation?.additionalSecurity ?? "TBD basis credit evaluation.",
      additionalCovenants: "",
      minimumPerformanceGuarantee:
        "Mandatorily to be explored with the OEM/ Supplier & AMC to be provided for full tenure of Lease",
      validityOfSanction: "This offer is valid for 60 days from the date of the facility letter.",
      smaReporting:
        "Lessee acknowledges that in the event of any default in payment obligations, The delay shall classified and reported as the account under SMA and/or NPA categories strictly in accordance with applicable RBI guidelines and prudential norms on Income Recognition, Asset Classification and Provisioning pertaining to Advances, as amended from time to time.",
      confidentiality:
        "This Term Sheet and its contents, including the existence of the proposed transaction and the commercial terms set out herein, are confidential and shall not be disclosed by the recipient to any third party without the prior written consent of the disclosing party, except to the recipient's directors, officers, employees, professional advisors, or lenders on a need-to-know basis and subject to equivalent confidentiality obligations.\n\nDisclosure may be made if required by applicable law, regulation, or order of a competent authority, provided that the recipient, to the extent legally permissible, gives prior notice to the disclosing party to enable it to seek protective measures. This confidentiality obligation shall survive the expiry or termination of this Term Sheet.",
      note: "The above terms are indicative and for discussion purposes only and are subject to change based on further due diligence including proposed Assets and internal approvals. The Sanction letter with detailed terms for financing shall be provided only after receiving complete information about the project from Sponsor and after taking all necessary internal approvals.",
    };
  }, [selectedCalculation, companyName, companyCity, term_sheet_id]);

  const [data, setData] = useState<TermsheetData>(initialData);

  // When drawer opens or calculation/company/term_sheet_id changes, sync form data so it reflects the latest
  useEffect(() => {
    if (drawerOpen && selectedCalculation) {
      setData(initialData);
    }
  }, [drawerOpen, selectedCalculation, initialData]);

  const monthQuarterText = selectedCalculation?.inputs?.rentalFrequency === "monthly" ? "month" : "quarter";
  const monthQuarterText2 = selectedCalculation?.inputs?.rentalFrequency === "monthly" ? "monthly" : "quarterly";
  const days = selectedCalculation?.inputs?.rentalFrequency === "monthly" ? "1/30" : "1/90";

  const [hasChanges, setHasChanges] = useState(false);
  const [status, setStatus] = useState<Status>(selectedCalculation?.status || "draft");
  const [customRows, setCustomRows] = useState<CustomRow[]>(() => {
    const rows = selectedCalculation?.customRows;
    if (!Array.isArray(rows)) return [];
    return rows.map((r: { label?: string; value?: string }) => ({
      label: typeof r?.label === "string" ? r.label : "",
      value: typeof r?.value === "string" ? r.value : "",
    }));
  });
  const [proRatedDay, setProRatedDay] = useState<number>(Number(selectedCalculation?.proRatedDay) || 5);
  const [editingProgramLimit, setEditingProgramLimit] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const { refreshWorkflow } = useCustomerWorkflow();
  // Sync customRows when selectedCalculation changes (e.g. opening a different calculation)
  useEffect(() => {
    const rows = selectedCalculation?.customRows || [];
    if (!Array.isArray(rows)) {
      setCustomRows([]);
      return;
    }
    setCustomRows(
      rows.map((r: { label?: string; value?: string }) => ({
        label: typeof r?.label === "string" ? r.label : "",
        value: typeof r?.value === "string" ? r.value : "",
      }))
    );
  }, [selectedCalculation?.id || '']);

  // Sync term_sheet_id when termSheetId prop changes (e.g. opening a calculation that already has a term sheet)
  useEffect(() => {
    setterm_sheet_id(termSheetId ?? null);
  }, [termSheetId]);

  const isEditable = status !== "approved" && status !== "rejected";

  const update = (field: keyof TermsheetData, value: string) => {
    if (!isEditable) return;
    setData((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!customerId || !selectedCalculation?.id) {
      toast.error("Customer or calculation not found.");
      return;
    }
    // Sync pro-rated day text into data so saved payload is correct
    const dataToSave: TermsheetData = {
      ...data,
      proRatedDay: Number(proRatedDay),
      customRows:customRows,
      rentalPayments: getRentalPaymentsText(proRatedDay, monthQuarterText, monthQuarterText2),
      proRatedRentals: getProRatedRentalsText(proRatedDay, monthQuarterText, monthQuarterText2, days),
      proRatedRentalsDate: `${dayOrdinal(proRatedDay)} day of each ${monthQuarterText}`,
    };
    const payload = {
      customerId,
      calculationId: selectedCalculation.id,
      termSheet: {
        data: dataToSave,
        customRows,
        proRatedDay,
        status,
      },
    };
    try {
      setIsSaving(true);
      await apiService.post<ApiResponse<unknown>>(
        "/customers/update-term-sheet-document",
        payload
      );
      setData(dataToSave);
      setHasChanges(false);
      toast.success("Termsheet changes saved successfully.");
      onSaveSuccess?.({
        programLimit: dataToSave.programLimit,
        customRows,
        proRatedDay,
        additionalSecurity: dataToSave.additionalSecurity,
      });
    } catch (err) {
      console.error("Failed to save termsheet:", err);
      toast.error("Could not save termsheet. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleAction = async (action: string) => {
    const statusMap: Record<string, Status> = {
      Approved: "approved",
      Rejected: "rejected",
      Shared: "shared",
    };
    const newStatus = statusMap[action];
    if (!newStatus) return;

    const id = selectedCalculation?.id;
    if (!id) {
      toast.error("Term sheet or calculation ID not found.");
      return;
    }

    try {
      setIsUpdatingStatus(true);
      await apiService.post<ApiResponse<unknown>>(
        `/customers/update-term-sheet-status/${selectedCalculation.id}`,
        { status: newStatus }
      );
      setStatus(newStatus);
      onSaveSuccess?.({ status: newStatus } as TermsheetSavedValues);
      toast.success(`Termsheet has been ${action.toLowerCase()}.`);
    } catch (err) {
      console.error("Failed to update termsheet status:", err);
      toast.error("Could not update status. Please try again.");
    } finally {
      setIsUpdatingStatus(false);
      await refreshWorkflow();
    }
  };

  const addCustomRow = () => {
    if (customRows.length >= 1) return;
    setCustomRows([{ label: "", value: "" }]);
    setHasChanges(true);
  };

  const removeCustomRow = () => {
    setCustomRows([]);
    setHasChanges(true);
  };

  const updateCustomRow = (index: number, field: "label" | "value", val: string) => {
    setCustomRows((prev) => prev.map((r, i) => (i === index ? { ...r, [field]: val } : r)));
    setHasChanges(true);
  };

  const isQuarterly = selectedCalculation?.inputs.rentalFrequency === "quarterly";
  const tenureLabel = isQuarterly ? "Tenure of Lease (In Quarter)" : "Tenure of Lease (In Months)";
  const rentalCostLabel = isQuarterly ? "Rental Cost - PTPQ (Excl GST)" : "Rental Cost - PTPM (Excl GST)";
  const finalLabel = isQuarterly ? "Final - PTPQ (Excl. GST)" : "Final - PTPM (Excl. GST)";
  const gstPassbackEnabled = selectedCalculation?.inputs.gstPassbackEnabled === true;

  const rows: { label: string; field: keyof TermsheetData; editable?: boolean; editType?: string; highlight?: boolean }[] = [
    { label: "Lessee Name", field: "lesseeName" },
    { label: "Lessor", field: "lessor" },
    { label: tenureLabel, field: "tenureOfLease" },
    { label: "Program Limit", field: "programLimit", editable: true },
    { label: "Security Deposit", field: "securityDeposit" },
    { label: rentalCostLabel, field: "rentalCostPTPM" },
    { label: "GST Rebate", field: "gstRebate" },
    { label: finalLabel, field: "finalPTPM", highlight: true },
    { label: "Processing Fees (Excl. GST)", field: "processingFees" },
    { label: "Advance Rental (Excl. GST)", field: "advanceRental" },
    { label: "Payment Frequency", field: "paymentFrequency" },
    { label: "Rental Payments", field: "rentalPayments" },
    { label: "Pro Rated Rentals", field: "proRatedRentals" },
    { label: "Foreclosure Charges", field: "foreclosureCharges" },
    { label: "Documentation Charges", field: "documentationCharges" },
    { label: "Insurance", field: "insurance" },
    { label: "Clear Title and Encumbrances", field: "clearTitle" },
    { label: "Economic Life Certification", field: "economicLife" },
    { label: "Penal Charges", field: "penalCharges" },
    { label: "Additional Security", field: "additionalSecurity", editable: true, editType: "textarea" },
    { label: "Minimum Performance Guarantee", field: "minimumPerformanceGuarantee" },
    { label: "Validity of Sanction", field: "validityOfSanction" },
    { label: "SMA/NPA Reporting", field: "smaReporting" },
    { label: "Confidentiality", field: "confidentiality" },
  ];

  const visibleRows = rows.filter((row) => {
    if (row.field === "gstRebate") {
      if (!gstPassbackEnabled) return false;
      const val = data[row.field];
      return val != null && String(val).trim() !== "";
    }
    if (row.field === "rentalCostPTPM") {
      const val = data[row.field];
      return val != null && String(val).trim() !== "";
    }
    if (row.field === "finalPTPM") {
      return gstPassbackEnabled;
    }
    if (row.field === "processingFees") {
      const pct = getSecurityDepositPct(data.processingFees);
      return pct > 0;
    }
    if (row.field === "advanceRental") {
      return String(data.advanceRental || "").trim() !== "";
    }
    return true;
  });

  const sc = statusConfig[status];

  // Generate day options 1-31
  const dayOptions = Array.from({ length: 31 }, (_, i) => i + 1);

  const generateTermSheet = async () => {
    try {
      const response = await apiService.post<any>(
        `/customers/generate-term-sheet/${selectedCalculation?.id}`,
        { id: selectedCalculation.id }
      );
      // API may return { status, data: { termSheetId, downloadedAt } } or flat { termSheetId, downloadedAt }
      const body = response as Record<string, unknown>;
      const inner =
        body?.data != null && typeof body.data === "object"
          ? (body.data as Record<string, unknown>)
          : body;
      const newTermSheetId =
        (inner?.termSheetId as string | undefined) ??
        (body?.termSheetId as string | undefined) ??
        null;
      const newDownloadedAt =
        (inner?.downloadedAt as string | undefined) ??
        (body?.downloadedAt as string | undefined) ??
        undefined;

      const httpOk =
        body?.status === 200 ||
        body?.status === 201 ||
        (typeof body?.status === "number" && (body.status as number) < 400);

      if (httpOk || newTermSheetId || newDownloadedAt) {
        toast.success("Term Sheet Generated Successfully");
        if (newTermSheetId != null && String(newTermSheetId).trim() !== "") {
          setterm_sheet_id(String(newTermSheetId));
        }
        setData((prev) => ({
          ...prev,
          ...(newDownloadedAt ? { date: newDownloadedAt } : {}),
          ...(newTermSheetId != null && String(newTermSheetId).trim() !== ""
            ? { refNo: String(newTermSheetId) }
            : {}),
        }));
        onSaveSuccess?.({
          ...(newTermSheetId != null && String(newTermSheetId).trim() !== ""
            ? { termSheetId: String(newTermSheetId) }
            : {}),
          ...(newDownloadedAt ? { downloadedAt: newDownloadedAt } : {}),
        });
      }
    } catch (err) {
      toast.error("Term Sheet Generation Failed");
    }finally{
      await refreshWorkflow();
    }
  };

  return (
      <Drawer open={drawerOpen} onOpenChange={(open) => { setDrawerOpen(open); if (!open) onClose(); }}>
        <DrawerContent className="h-[98vh] max-h-[98vh] ms-auto w-[95%] rounded-t-xl flex flex-col overflow-hidden">
        <VisuallyHidden><DrawerTitle>Termsheet Preview</DrawerTitle></VisuallyHidden>
        {optionalHeaderSlot && <div className="shrink-0">{optionalHeaderSlot}</div>}
        <div className="flex flex-col bg-background flex-1 min-h-0">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-6 py-4 bg-card shrink-0">
        <div className="flex items-center gap-3">
          <div>
            <div className="flex items-center gap-2.5">
              <h2 className="text-lg font-semibold text-foreground">Termsheet Preview</h2>
              <Badge variant="outline" className={cn("text-[11px] font-medium", sc.className)}>
                {sc.label}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">{data.refNo} · {data.lesseeName}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {!term_sheet_id ? (
            <Button variant="outline" size="sm" onClick={generateTermSheet}>
              <Download className="h-4 w-4 mr-2" /> Generate Term Sheet
            </Button>
          ) : (
            <>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline" className="gap-1.5">
                    <Download className="h-3.5 w-3.5" />
                    Download
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={async () => {
                    const logoDataUrl = await getLogoDataUrl();
                    await generateTermsheetPDF(
                      data,
                      customRows,
                      status === "approved" ? "Termsheet" : "Indicative Termsheet",
                      logoDataUrl,
                      {
                        gstPassbackEnabled: selectedCalculation?.inputs.gstPassbackEnabled === true,
                        isQuarterly: selectedCalculation?.inputs.rentalFrequency === "quarterly",
                      }
                    );
                  }}>
                    Download PDF
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={async () => {
                    const logoDataUrl = await getLogoDataUrl();
                    generateTermsheetDocx(
                      data,
                      customRows,
                      status === "approved" ? "Termsheet" : "Indicative Termsheet",
                      logoDataUrl,
                      {
                        gstPassbackEnabled: selectedCalculation?.inputs.gstPassbackEnabled === true,
                        isQuarterly: selectedCalculation?.inputs.rentalFrequency === "quarterly",
                      }
                    );
                  }}>
                    Download DOCX
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {status === "approved" ? (
                <Button size="sm" variant="outline" className="gap-1.5 text-[hsl(var(--success))] border-[hsl(var(--success))]/30 cursor-default pointer-events-none">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Approved
                </Button>
              ) : status === "rejected" ? (
                <Button size="sm" variant="outline" className="gap-1.5 text-destructive border-destructive/30 cursor-default pointer-events-none">
                  <Ban className="h-3.5 w-3.5" />
                  Rejected
                </Button>
              ) : (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="outline" className="gap-1.5" disabled={isUpdatingStatus}>
                      Actions
                      <ChevronDown className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {/* <DropdownMenuItem onClick={() => handleAction("Shared")} className="gap-2">
                      <Share2 className="h-3.5 w-3.5" />
                      Share
                    </DropdownMenuItem> */}
                    <DropdownMenuItem onClick={() => handleAction("Approved")} className="text-[hsl(var(--success))] gap-2" disabled={isUpdatingStatus}>
                      <Check className="h-3.5 w-3.5" />
                      Approve
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleAction("Rejected")} className="text-destructive gap-2" disabled={isUpdatingStatus}>
                      <Ban className="h-3.5 w-3.5" />
                      Reject
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </>
          )}
        </div>
      </div>

      {/* Content - single scroll area */}
      <div className="flex-1 min-h-0 overflow-y-auto bg-muted/30">
        <div className="mx-auto max-w-4xl px-6 py-8">
          <div className="bg-card rounded-lg border border-border shadow-lg p-8 md:p-10">
            {/* Document Header */}
            <div className="mb-8 text-center">
              <img
                src={AstrafinLogo}
                alt="Astrafin"
                className="mx-auto mb-6 h-10 w-auto max-w-[220px] object-contain"
              />
            </div>

            <div className="flex justify-between mb-2 text-sm">
              <div>
                <p>To,</p>
                <p className="font-medium">{data.lesseeName}</p>
                <p>{data.lesseeAddress}</p>
                <p className="text-sm">Date: {formatDateDDMMYYYY(data.date)}</p>
              </div>
            </div>

            <p className="text-sm mb-4">Ref. No: {data.refNo}</p>

            <h2 className="text-center text-base font-bold underline mb-4">
              {status === "approved" ? "Termsheet" : "Indicative Termsheet"}
            </h2>

            <p className="text-sm font-semibold underline mb-2">
              Sub: Asset Leasing Facility for {data.lesseeName}
            </p>
            <p className="text-sm mb-6 text-muted-foreground">
              Astrafin Neosolutions Private Limited hereby offers an asset leasing facility, subject to the terms and conditions set out below.
            </p>

            {/* Termsheet Table */}
            <table className="ts-document-table mb-6">
              <tbody>
                {visibleRows.map((row) => {
                  // Pro Rated Rentals - day dropdown in header
                  if (row.field === "proRatedRentals") {
                    return (
                      <tr key={row.field}>
                        <td>
                          <div className="flex flex-col gap-2">
                            <span>Pro Rated Rentals</span>
                            {isEditable ? (
                              <Select
                                value={String(proRatedDay)}
                                onValueChange={(val) => {
                                  const d = Number(val);
                                  setProRatedDay(d);
                                  setData((prev) => ({
                                    ...prev,
                                    rentalPayments: getRentalPaymentsText(d, monthQuarterText, monthQuarterText2),
                                    proRatedRentals: getProRatedRentalsText(d, monthQuarterText, monthQuarterText2, days),
                                    proRatedRentalsDate: `${dayOrdinal(d)} day of each ${monthQuarterText}`,
                                  }));
                                  setHasChanges(true);
                                }}
                              >
                                <SelectTrigger className="w-full h-8 text-xs">
                                  <SelectValue placeholder="Day" />
                                </SelectTrigger>
                                <SelectContent>
                                  {dayOptions.map((d) => (
                                    <SelectItem key={d} value={String(d)}>
                                      {d}{d === 1 ? "st" : d === 2 ? "nd" : d === 3 ? "rd" : "th"} of month
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <span className="text-xs text-muted-foreground">
                                Due: {proRatedDay}{proRatedDay === 1 ? "st" : proRatedDay === 2 ? "nd" : proRatedDay === 3 ? "rd" : "th"} of month
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <span className="whitespace-pre-line">
                            {getProRatedRentalsText(proRatedDay, monthQuarterText, monthQuarterText2, days)}
                          </span>
                        </td>
                      </tr>
                    );
                  }

                  // Rental Payments - display text driven by selected proRatedDay (same day as Pro Rated Rentals)
                  if (row.field === "rentalPayments") {
                    return (
                      <tr key={row.field}>
                        <td>{row.label}</td>
                        <td>
                          <span className="whitespace-pre-line">
                            {getRentalPaymentsText(proRatedDay, monthQuarterText, monthQuarterText2)}
                          </span>
                        </td>
                      </tr>
                    );
                  }

                  // Program Limit - formatted display, click to edit
                  if (row.field === "programLimit") {
                    return (
                      <tr key={row.field}>
                        <td>{row.label}</td>
                        <td>
                          {isEditable ? (
                            editingProgramLimit ? (
                              <Input
                                autoFocus
                                value={data.programLimit}
                                onChange={(e) => update("programLimit", e.target.value)}
                                onBlur={() => setEditingProgramLimit(false)}
                                onKeyDown={(e) => e.key === "Enter" && setEditingProgramLimit(false)}
                                className="text-sm"
                                placeholder="Enter amount"
                              />
                            ) : (
                              <button
                                type="button"
                                onClick={() => setEditingProgramLimit(true)}
                                className="text-left w-full px-3 py-1.5 rounded-md border border-transparent hover:border-border hover:bg-muted/50 transition-colors text-sm font-medium cursor-pointer"
                              >
                                {formatIndianCurrency(data.programLimit)}
                              </button>
                            )
                          ) : (
                            <span>{formatIndianCurrency(data.programLimit)}</span>
                          )}
                        </td>
                      </tr>
                    );
                  }

                  // Security Deposit - show percentage and calculated amount from program limit
                  if (row.field === "securityDeposit") {
                    // const pct = getSecurityDepositPct(data.securityDeposit);
                    // const programLimitNum = getProgramLimitNumeric(data.programLimit);
                    // const calculatedAmount = pct > 0 && programLimitNum > 0 ? programLimitNum * (pct / 100) : null;
                    const displayValue = data.securityDeposit
                    return (
                      <tr key={row.field}>
                        <td>{row.label}</td>
                        <td>
                          <span>{displayValue}</span>
                        </td>
                      </tr>
                    );
                  }

                  // Processing Fees - show percentage and calculated amount from program limit: "1.00% (INR X crores + GST)"
                  if (row.field === "processingFees") {
                    const pct = getSecurityDepositPct(data.processingFees);
                    const programLimitNum = getProgramLimitNumeric(data.programLimit);
                    const calculatedAmount = pct > 0 && programLimitNum > 0 ? programLimitNum * (pct / 100) : null;
                    const displayValue =
                      calculatedAmount != null
                        ? `${data.processingFees} (${formatInrLakhsCrores(calculatedAmount)} + GST)`
                        : data.processingFees;
                    return (
                      <tr key={row.field}>
                        <td>{row.label}</td>
                        <td>
                          <span>{displayValue}</span>
                        </td>
                      </tr>
                    );
                  }

                  const cellValue = data[row.field];
                  let displayStr = typeof cellValue === "string" ? cellValue : typeof cellValue === "number" ? String(cellValue) : "";
                  if (row.field === "tenureOfLease") {
                    const num = parseFloat(String(cellValue).replace(/[^0-9.]/g, ""));
                    if (!Number.isNaN(num)) {
                      displayStr = isQuarterly
                        ? `${Math.round(num / 3)} Quarter`
                        : `${Math.round(num)} Month`;
                    }
                  }
                  return (
                    <tr key={row.field} className={row.highlight ? "ts-highlight font-bold" : ""}>
                      <td className={row.highlight ? "font-bold" : undefined}>{row.label}</td>
                      <td className={row.highlight ? "font-bold" : undefined}>
                        {row.editable && isEditable ? (
                          row.editType === "textarea" ? (
                            <Textarea
                              value={displayStr}
                              onChange={(e) => update(row.field, e.target.value)}
                              className="min-h-[80px] text-sm"
                            />
                          ) : (
                            <Input
                              value={displayStr}
                              onChange={(e) => update(row.field, e.target.value)}
                              className="text-sm"
                            />
                          )
                        ) : (
                          <span className="whitespace-pre-line">{displayStr}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}

                {/* Custom rows (max 1) */}
                {customRows.map((cr, i) => (
                  <tr key={`custom-${i}`}>
                    <td>
                      <div className="flex items-center gap-2">
                        {isEditable ? (
                          <>
                            <Input
                              placeholder="Field name"
                              value={cr.label}
                              onChange={(e) => updateCustomRow(i, "label", e.target.value)}
                              className="text-sm font-semibold flex-1"
                            />
                            <button
                              type="button"
                              onClick={removeCustomRow}
                              className="text-destructive hover:text-destructive/80 transition-colors p-1 rounded-md hover:bg-destructive/10"
                              title="Remove row"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </>
                        ) : (
                          <span>{cr.label || "—"}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      {isEditable ? (
                        <Textarea
                          placeholder="Enter details..."
                          value={cr.value}
                          onChange={(e) => updateCustomRow(i, "value", e.target.value)}
                          className="min-h-[60px] text-sm"
                        />
                      ) : (
                        <span className="whitespace-pre-line">{cr.value || "—"}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Add Terms button (only if no custom row yet) */}
            {isEditable && customRows.length < 1 && (
              <div className="mb-8">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addCustomRow}
                  className="gap-1.5 text-muted-foreground hover:text-foreground"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add Terms
                </Button>
              </div>
            )}

            {/* Note */}
            <p className="text-xs italic text-muted-foreground mb-8">
              Note: {data.note}
            </p>

            {/* Signature */}
            <div className="mt-12 mb-8">
              <p className="text-sm mb-8">Accepted and Signed by</p>
              <p className="border-b border-foreground w-64 mb-4">&nbsp;</p>
              <p className="text-sm">Name:</p>
              <p className="text-sm">Designation:</p>
              <p className="text-sm">Date:</p>
            </div>

            {/* Save button */}
            {hasChanges && isEditable && (
              <div className="flex justify-end pt-4 border-t border-border">
                <Button onClick={handleSave} className="gap-1.5" disabled={isSaving}>
                  <Save className="h-4 w-4" />
                  {isSaving ? "Saving…" : "Save Changes"}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default TermsheetDrawer;
